/**
 * The connection `system_type` vocabulary, in one place.
 *
 * The scale is `AgentSystemType` (backend/app/models/agent_connection.py) — a closed enum whose
 * three arms every projection that carries a connection row emits verbatim
 * (`employee_workspace`:342 `capabilities.connections[].system_type`,
 * `routers/agents.py`:4253 `connection-readiness.connections[].system_type`). The token is a
 * machine identifier; it is never words a technician reads.
 *
 * `EmployeeWorkspace` used to hold this map itself, keyed `eset_protect / microsoft_365 /
 * m365_graph / teamviewer / openclaw / acronis`. Two of those are the *only* producer's arms
 * spelled correctly; `acronis_backup` — the arm the CONNECTION-HUB-001 boundary added so an admin
 * can hold an Acronis connection row today — had no key at all, and `acronis`/`microsoft_365`/
 * `teamviewer`/`openclaw` are not tokens this producer can mint (they are specialist-domain and
 * remote-transport spellings from other scales in the same file). Because the map and every test
 * fixture were written on the map's own scale, nothing caught it: an `acronis_backup` connection
 * printed as `acronis_backup` on the three surfaces that have no registry display name, and was
 * filtered out entirely by the specialist-domain matrix, which then reported "no connection
 * configured for this domain" while the Acronis card on the same page showed that connection.
 *
 * So this map is exactly the producer's arm set — no key the producer cannot mint, because a key
 * it cannot mint is a key that lets a test written against this map pass while the real token
 * falls through. `systemTypeVocabulary.test.js` re-derives the arm set from the Python enum and
 * reddens if a fourth arm appears without a label here.
 */

export const SYSTEM_TYPE_HE = {
  m365_graph: "Microsoft Graph (M365)",
  eset_protect: "ESET PROTECT",
  // The connector registry's own display_name for this connector, and the same string the two
  // other system-type maps in the app carry (EmployeeConnectionHub, AgentDetailPage).
  acronis_backup: "Acronis Backup",
};

/**
 * The token, or — for a token nobody has written a label for yet — the token itself. Echoing an
 * unmapped identifier is the honest failure and keeps it inspectable; what is not allowed is
 * reaching that arm for a token this map is supposed to cover, which the parity guard prevents.
 */
export function systemTypeLabel(value) {
  if (value == null || value === "") return null;
  const token = String(value);
  return SYSTEM_TYPE_HE[token] || token;
}
